import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { CommunityOccupancy, CommunityType } from 'types/communities';
import { CommunityContract, CommunityGroupedContract } from 'types/contracts';
import { formatDistanceFromKm } from 'utils/helpers/units';

import { isOverallApartmentType } from './apartmentTypes';
import { COMMUNITY_CONTRACT_TYPE } from '../../constants';

export const processFullHomeCommunities = (data: any) => {
  const communityMap = (data ?? []).reduce((acc: any, community: CommunityType) => {
    acc[community?.id] = community;
    return acc;
  }, {});
  const distanceMap = {} as { [key: string]: number };

  const communities = (data ?? []).filter(
    (community: CommunityType) => community.company_relation_type === COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
  );
  const competitors = (data ?? []).filter(
    (community: CommunityType) => community.company_relation_type === COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR
  );

  return {
    communities,
    competitors,
    communityMap,
  };
};

export const DEFAULT_SWOT_SCORES = {
  aa_apartment_size: 3,
  aa_apartment_flooring: 3,
  aa_kitchen_cabinets_countertops: 3,
  aa_apartment_views: 3,
  aa_apartment_lighting: 3,
  aa_washer_dryer_laundry: 3,
  aa_cable_internet_access: 3,
  ca_fitness_exercise_space: 3,
  ca_indoor_amenities: 3,
  ca_laundry_room: 3,
  ca_lobby_hallway_aesthetics: 3,
  ca_onsite_physical_therapy: 3,
  ca_outdoor_amenities: 3,
  ca_pet_friendliness: 3,
  ca_serene_outdoor_space: 3,
  de_service_experience: 3,
  de_multiple_dining_venues: 3,
  de_menu_options: 3,
  de_food_quality: 3,
  de_dining_hours: 3,
  cs_breadth_of_clinical_services: 3,
  cs_nursing_coverage: 3,
  cs_state_survey_results: 3,
  cs_care_staff_ratio: 3,
  ap_diversity_of_programming: 3,
  ap_outings: 3,
  ap_unique_programming: 3,
  ap_transportation_services: 3,
  ap_physical_exercise_programming: 3,
  ap_spiritual_religious_programming: 3,
  reputation_general: 3,
  reputation_google_rating: 3,
  reputation_caring_com: 3,
  reputation_place_for_mom_rating: 3,
  community_feel_first_impressions: 3,
  community_feel_front_desk_experience: 3,
  community_feel_associate_engagement: 3,
  community_feel_associate_tenure: 3,
  location_healthcare_services: 3,
  location_shopping_options: 3,
  po_competitive_pricing: 3,
  po_access_to_government_funding: 3,
  po_care_pricing: 3,
  internet_seo_ranking: 3,
  internet_website_design: 3,
  internet_website_content: 3,
  internet_social_media: 3,
  internet_email_marketing_campaigns: 3,
  internet_community_pictures_videos: 3,
  internet_access_to_virtual_tour: 3,
  internet_website_navigation: 3,
  cs_levels_of_care: 3,
  community_feel_associate_knowledge: 3,
  po_reaccuring_fees: 3,
  internet_traditional_marketing: 3,
  apartment_amenities_score: 3,
  care_services_score: 3,
  community_amenities_score: 3,
  community_feel_score: 3,
  dining_experience_score: 3,
  internet_social_media_score: 3,
  life_enrichment_score: 3,
  location_score: 3,
  pricing_score: 3,
  reputation_score: 3,
  overall_score: 3,
  apartment_amenities_note: null,
  care_services_note: null,
  community_amenities_note: null,
  community_feel_note: null,
  dining_experience_note: null,
  internet_social_media_note: null,
  life_enrichment_note: null,
  location_note: null,
  pricing_note: null,
  reputation_note: null,
};

export const getSwotScoresOutboundPayload = ({
  competitorId,
  competitorName,
  communityId,
  communityName,
  emailAddress,
  id,
}: {
  competitorId: number;
  competitorName: string;
  communityId: number;
  communityName: string;
  emailAddress: string;
  id: number;
}) => {
  return {
    competitor_id: competitorId,
    competitor_name: competitorName,
    competitor: competitorId,
    email_address: emailAddress,
    my_community_id: communityId,
    my_community: communityId,
    my_community_name: communityName,
    id,
    timestamp: new Date().toISOString(),
  };
};

export const groupAndSortCommunityContracts = (contractType: CommunityContract[]): CommunityGroupedContract[] => {
  const grouped = Object.values(
    contractType.reduce<Record<number, CommunityGroupedContract>>((acc, contract) => {
      if (!acc[contract.type]) {
        acc[contract.type] = {
          name: contract.name.replace(/^Contract\s*/, '').trim(),
          type: contract.type,
          percentages: [],
        };
      }
      acc[contract.type].percentages.push(contract.refund_percentage);
      return acc;
    }, {})
  );

  return grouped
    .map((group) => ({
      ...group,
      percentages: group.percentages.sort((a, b) => a - b),
    }))
    .sort((a, b) => {
      if (a.type === COMMUNITY_CONTRACT_TYPE.RENTAL) return 1;
      if (b.type === COMMUNITY_CONTRACT_TYPE.RENTAL) return -1;
      return a.name.localeCompare(b.name);
    });
};

export const calculateOccupancy = (occupancies: CommunityOccupancy[]) => {
  type OccupancyWithSnakeCase = CommunityOccupancy & { apartment_type_id?: number };

  const getApartmentTypeId = (occupancy: OccupancyWithSnakeCase) => {
    const rawId = occupancy?.apartmentTypeId ?? occupancy?.apartment_type_id;
    return rawId;
  };

  const isOverall = (occupancy: OccupancyWithSnakeCase) =>
    isOverallApartmentType({ id: getApartmentTypeId(occupancy) });

  const occupanciesByLivingType = occupancies.reduce<Record<string, CommunityOccupancy[]>>((acc, occupancy) => {
    const key = String(occupancy.livingTypeId);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(occupancy);
    return acc;
  }, {});

  // Per living type:
  // - if OVERALL exists, ignore all room-type rows for that living type
  // - if OVERALL does not exist, aggregate all room-type rows
  const entries = Object.values(occupanciesByLivingType).flatMap((livingTypeEntries) => {
    const overallEntries = livingTypeEntries.filter((entry) => isOverall(entry));
    return overallEntries.length ? overallEntries : livingTypeEntries.filter((entry) => !isOverall(entry));
  });

  const allHaveTotalUnits = entries.every((o) => o.totalApartmentsUnits != null && o.freeApartmentsUnits != null);

  if (allHaveTotalUnits) {
    const totalUnits = entries.reduce((sum, o) => sum + (o.totalApartmentsUnits ?? 0), 0);
    const totalFree = entries.reduce((sum, o) => sum + (o.freeApartmentsUnits ?? 0), 0);

    const occupiedUnits = totalUnits - totalFree;
    const occupancyPercentage = totalUnits > 0 ? ((occupiedUnits / totalUnits) * 100).toFixed() : '0';

    return { totalUnits, occupiedUnits, occupancyPercentage };
  } else {
    const validPercentages = entries.map((o) => parseFloat(o.occupancyPercentage)).filter((p) => !isNaN(p));

    const avgPercentage =
      validPercentages.length > 0
        ? Math.round(validPercentages.reduce((sum, p) => sum + p, 0) / validPercentages.length).toString()
        : '0';

    return { totalUnits: null, occupiedUnits: null, occupancyPercentage: avgPercentage };
  }
};

export const calculateCommunityDistance = (distanceKm: number, unitSource?: any) => {
  return formatDistanceFromKm(distanceKm, unitSource, 2);
};
